/**
 * INSTANT SWAP (FR-U-TXN-010, FRS 1.2 domain LQD).
 *
 * The second peer-to-peer flow, beside the offer book at /p2p. The difference is the whole screen:
 *
 *   /p2p    willing buyer, willing seller. You find a counterparty, agree a price, escrow holds
 *           until somebody confirms. Best price, if you can wait.
 *   /swap   you buy from VASP's own prefunded float, at the rate published for the day, and
 *           it settles immediately. Certain price, now, and you pay the spread.
 *
 * Three rules this screen is judged against, all of them easy to lose while making it look tidy:
 *
 *   1. THE SPREAD IS ITEMISED (FR-U-LQD-001). Four lines: rate, spread, fee, you receive. A screen
 *      showing only a total has hidden what the platform earns inside the price, which is the
 *      practice the CMA conduct rules exist to stop. The API returns them as four separate fields
 *      precisely so this component cannot accidentally present one.
 *
 *   2. UNAVAILABLE IS EXPLAINED (FR-U-LQD-002). Eight refusals, each with a different next step.
 *      "Instant settlement unavailable" is not one of them. Six leave the ordinary route open and
 *      the screen offers it.
 *
 *   3. THE RATE IS FIXED FOR THE DAY *AND* MAY BE WITHDRAWN (FR-U-LQD-004). Both halves. A board
 *      advertised as unconditional and then pulled mid-day is worse than one honest about its
 *      condition, because the participant planned around it.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  api,
  refusalOf,
  standardPathAvailable,
  type LiquidityRefusal,
  type SwapBoard,
  type SwapQuote,
} from '@/lib/api';
import { ErrorState, LoadingCard } from '@/components/states';
import { formatAssetAmount, sanitizeAmountTyping } from '@/lib/money';
import { newIdempotencyKey } from '@/lib/ids';
import { useResource } from '@/lib/use-resource';
import { LiveRates } from '@/components/LiveRates';

/** The settlement currency. A pair quoted in it is bought with cash; anything else is a conversion. */
const FIAT = 'KES';

/**
 * What the participant is told, and what they can do next.
 *
 * The backend already sends a disclosure sentence and we show it: it is the same sentence a
 * regulator would read, and paraphrasing it here is how the meaning would quietly drift. What this
 * table adds is the TITLE and the ACTION, which are presentation decisions.
 */
const REFUSAL_UI: Record<LiquidityRefusal, { title: string; canRetry: boolean }> = {
  no_board_rate: { title: 'No rate published today', canRetry: false },
  board_withdrawn: { title: "Today's rate was withdrawn", canRetry: true },
  daily_volume_cap: { title: "Today's limit reached", canRetry: false },
  insufficient_float: { title: 'Not available for this amount', canRetry: false },
  exposure_cap: { title: 'Instant swap is paused', canRetry: false },
  suspended: { title: 'Instant swap is paused', canRetry: true },
  stale_rates: { title: 'We cannot confirm a price', canRetry: true },
  quote_expired: { title: 'That price has expired', canRetry: true },
};

interface Refusal {
  kind: LiquidityRefusal;
  message: string;
  standardPath: boolean;
}

function readRefusal(error: unknown): Refusal | null {
  const kind = refusalOf(error);
  if (!kind) return null;
  return {
    kind,
    message: error instanceof Error ? error.message : 'Instant swap is not available right now.',
    standardPath: standardPathAvailable(error),
  };
}

export default function Swap() {
  const navigate = useNavigate();
  const { data: board, loading, error, reload } = useResource<SwapBoard>(() => api.swapBoard(), []);

  const [pair, setPair] = useState<{ base: string; quote: string } | null>(null);
  const [amount, setAmount] = useState('');
  const [quote, setQuote] = useState<SwapQuote | null>(null);
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const [pending, setPending] = useState(false);

  // Default to the first published pair once the board arrives.
  useEffect(() => {
    if (!pair && board?.rates.length) {
      setPair({ base: board.rates[0].base, quote: board.rates[0].quote });
    }
  }, [board, pair]);

  const rate = useMemo(
    () => board?.rates.find((r) => r.base === pair?.base && r.quote === pair?.quote) ?? null,
    [board, pair],
  );

  /**
   * The board, split into the two things a participant is actually choosing between.
   *
   * `FIAT` is the one currency the platform settles cash in. A pair quoted in it is a purchase with
   * money; anything else is a conversion between two virtual assets. Deriving the split rather than
   * listing it means a new pair needs no change here.
   */
  const cashPairs = useMemo(() => (board?.rates ?? []).filter((r) => r.quote === FIAT), [board]);
  const convertPairs = useMemo(() => (board?.rates ?? []).filter((r) => r.quote !== FIAT), [board]);
  const isCash = pair?.quote === FIAT;

  // Re-price whenever the amount settles. The quote is recorded server-side, so this is not free:
  // debounce rather than firing on every keystroke.
  useEffect(() => {
    if (!pair || !amount || Number(amount) <= 0) {
      setQuote(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        setRefusal(null);
        setQuote(await api.swapQuote(pair.quote, pair.base, amount, 'buy'));
      } catch (e) {
        setQuote(null);
        setRefusal(readRefusal(e));
      }
    }, 350);
    return () => clearTimeout(t);
  }, [pair, amount]);

  const confirm = useCallback(async () => {
    if (!quote) return;
    setPending(true);
    setRefusal(null);
    try {
      // Generated ONCE, here, and reused on any retry. A fresh key per attempt would make two
      // attempts two different swaps.
      const result = await api.swap(quote.quote_id, newIdempotencyKey());
      navigate(`/activity/${result.correlation_id}`);
    } catch (e) {
      setRefusal(readRefusal(e));
      // The quote is consumed server-side even on a refusal after claiming, so never offer the same
      // one again: re-price instead.
      setQuote(null);
    } finally {
      setPending(false);
    }
  }, [quote, navigate]);

  if (error) return <ErrorState body="We could not load today's rates." onRetry={reload} />;
  if (loading) return <LoadingCard lines={5} />;

  const noBoard = !board?.date || board.rates.length === 0;

  return (
    <div className="mx-auto max-w-lg space-y-5">
      <header>
        <h1 className="text-page-title text-ink">Instant swap</h1>
        <p className="mt-0.5 text-sm text-ink-muted">
          Buy an asset with cash, or convert one asset into another, from VASP&rsquo;s own
          float at today&rsquo;s rate. Settles immediately.
        </p>
      </header>

      {/*
        The streaming board and instant converter. It reads the same rate the swap will settle at
        and prices the charge with it, so a participant sees the number before they commit rather
        than after. Its own socket, so a dropped connection degrades this panel and nothing else.
      */}
      <LiveRates />

      {/* Nothing published. Say so; do NOT fall back to a live market number we have not committed to. */}
      {noBoard ? (
        <div className="rounded-card border border-border bg-surface p-5 text-center shadow-card">
          <p className="text-sm font-medium text-ink">No rate published today</p>
          <p className="mt-1 text-sm text-ink-muted">
            Instant swap is not available until a rate is published. You can still send and receive
            as usual.
          </p>
          <button
            onClick={() => navigate('/p2p')}
            className="mt-4 rounded-field border border-border bg-surface px-4 py-2 text-sm font-medium text-ink transition hover:bg-surface-sunken"
          >
            Trade with people instead
          </button>
        </div>
      ) : (
        <>
          {/* THE ACTION, THEN THE AMOUNT. */}
          <div className="rounded-card border border-border bg-surface p-4 shadow-card">
            <label htmlFor="swap-action" className="text-sm font-medium text-ink">
              Select action
            </label>

            {/*
              THE LIST IS DERIVED FROM THE BOARD, NEVER WRITTEN HERE.

              The board is the treasury's own statement of what it will trade today. An asset it has
              not priced cannot be bought, so a hardcoded menu would offer things the next call
              refuses -- and would keep offering them after a pair was withdrawn. Publish a pair and
              it appears; withdraw it and it is gone, with no deploy.

              The two groups are a property of the PAIR, not a second feature: a pair quoted in KES is
              a cash purchase, a pair quoted in another virtual asset is a conversion. Same endpoint,
              same escrow, same float.
            */}
            <select
              id="swap-action"
              aria-label="Select action"
              value={`${pair?.base}/${pair?.quote}`}
              onChange={(e) => {
                const [base, quoteAsset] = e.target.value.split('/');
                setPair({ base, quote: quoteAsset });
                // A new pair prices a new amount. Carrying the old quote across would show a figure
                // struck against a rate the participant is no longer buying at.
                setQuote(null);
                setRefusal(null);
              }}
              className="mt-1 w-full rounded-field border border-border bg-surface px-3 py-2 text-sm text-ink"
            >
              {cashPairs.length > 0 && (
                <optgroup label="Buy with cash">
                  {cashPairs.map((r) => (
                    <option key={`${r.base}/${r.quote}`} value={`${r.base}/${r.quote}`}>
                      Buy {r.base} with cash ({r.quote})
                    </option>
                  ))}
                </optgroup>
              )}
              {convertPairs.length > 0 && (
                <optgroup label="Swap / convert">
                  {convertPairs.map((r) => (
                    <option key={`${r.base}/${r.quote}`} value={`${r.base}/${r.quote}`}>
                      Convert {r.quote} to {r.base}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>

            <div className="mt-4 flex items-center justify-between gap-3">
              <label htmlFor="swap-amount" className="text-sm font-medium text-ink">
                You pay
              </label>
              <span className="rounded-pill bg-surface-sunken px-2 py-0.5 text-micro font-medium text-ink-muted">
                {isCash ? 'Buy with cash' : 'Convert'}
              </span>
            </div>

            <div className="mt-2 flex items-baseline gap-2">
              <input
                id="swap-amount"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(sanitizeAmountTyping(e.target.value))}
                placeholder="0.00"
                className="tabular w-full min-w-0 bg-transparent text-3xl font-semibold text-ink outline-none placeholder:text-ink-subtle"
              />
              <span className="shrink-0 text-lg font-medium text-ink-muted">{pair?.quote}</span>
            </div>

            <p className="mt-2 text-xs text-ink-subtle">
              {isCash
                ? `Paid from your ${pair?.quote} balance. You receive ${pair?.base}.`
                : `Converted from your ${pair?.quote} balance into ${pair?.base}.`}
            </p>
          </div>

          {/* THE FOUR FIGURES (FR-U-LQD-001). Never collapsed into one. */}
          {quote && (
            <div className="rounded-card border border-border bg-surface p-4 shadow-card">
              <p className="text-xs font-medium uppercase tracking-wide text-ink-subtle">
                Today&rsquo;s rate
              </p>
              {/*
                THESE FIGURES ARRIVE AS MINOR UNITS. `Quote.to_json` in platform/treasury.py sends
                `str(self.to_amount)`, an integer count, with no display field -- unlike the convert
                quote, which sends one. Rendered raw, "you receive 1000000 USDT" is what a screen says
                about one dollar. `formatAssetAmount` puts the point where the asset's scale says it
                goes; the right long-term fix is a `display` field on the quote (see lib/money.ts).
              */}
              <dl className="mt-3 space-y-2 text-sm">
                <div className="flex justify-between gap-3">
                  <dt className="text-ink-muted">Rate</dt>
                  <dd className="tabular text-right text-ink">
                    1 {quote.to.asset} = {Number(quote.rate).toLocaleString()} {quote.from.asset}
                  </dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-ink-muted">
                    Spread ({(quote.spread_bps / 100).toFixed(2)}%)
                  </dt>
                  <dd className="tabular text-right text-ink">
                    -{formatAssetAmount(quote.spread, quote.to.asset)} {quote.to.asset}
                  </dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-ink-muted">Fee</dt>
                  <dd className="tabular text-right text-ink">
                    {formatAssetAmount(quote.fee, quote.to.asset)} {quote.to.asset}
                  </dd>
                </div>
                <div className="flex justify-between gap-3 border-t border-border pt-2">
                  <dt className="font-medium text-ink">You receive</dt>
                  <dd className="tabular text-right font-semibold text-ink">
                    {formatAssetAmount(quote.you_receive, quote.to.asset)} {quote.to.asset}
                  </dd>
                </div>
              </dl>

              {/* FR-U-LQD-004, both halves. Fixed AND withdrawable. */}
              <p className="mt-3 rounded-field bg-surface-sunken p-2.5 text-xs text-ink-muted">
                Fixed for today ({quote.board_date}). May be withdrawn if the market moves sharply.
              </p>
            </div>
          )}

          {/* REFUSAL. Its own state, never a toast: it has a next step. */}
          {refusal && (
            <div className="rounded-card border border-warning/30 bg-warning/5 p-4">
              <p className="text-sm font-medium text-ink">{REFUSAL_UI[refusal.kind].title}</p>
              {/* The backend's own sentence, shown verbatim. Paraphrasing is where meaning drifts. */}
              <p className="mt-1 text-sm text-ink-muted">{refusal.message}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {REFUSAL_UI[refusal.kind].canRetry && (
                  <button
                    onClick={() => setAmount((a) => a)}
                    className="rounded-field border border-border bg-surface px-3.5 py-2 text-sm font-medium text-ink transition hover:bg-surface-sunken"
                  >
                    Try again
                  </button>
                )}
                {/* Six of the eight leave the ordinary route open. Offer it rather than dead-ending. */}
                {refusal.standardPath && (
                  <button
                    onClick={() => navigate('/p2p')}
                    className="rounded-field border border-border bg-surface px-3.5 py-2 text-sm font-medium text-ink transition hover:bg-surface-sunken"
                  >
                    Trade with people instead
                  </button>
                )}
              </div>
            </div>
          )}

          <button
            disabled={!quote || pending}
            onClick={confirm}
            className="w-full rounded-field bg-primary px-4 py-3 text-sm font-medium text-white transition hover:bg-primary-light disabled:opacity-40"
          >
            {pending ? 'Settling...' : 'Confirm'}
          </button>

          {rate && (
            <p className="text-center text-xs text-ink-subtle">
              Board published {new Date(rate.published_at).toLocaleTimeString()}. Everyone gets the
              same rate today.
            </p>
          )}
        </>
      )}
    </div>
  );
}
